/**
 * Downscale a photo in the browser before upload.
 *
 * Cloudflare's image resizing is a paid add-on, and a 12MP phone photo has no
 * business sitting in R2 to be shown as a 160px card. Resizing here keeps the
 * bucket small, uploads fast on a phone, and costs nothing.
 */

const MAX_EDGE = 800
const QUALITY = 0.82

/** Prefer WebP where the browser can encode it, else fall back to JPEG. */
function pickType(): 'image/webp' | 'image/jpeg' {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = 1
  return canvas.toDataURL('image/webp').startsWith('data:image/webp')
    ? 'image/webp'
    : 'image/jpeg'
}

/** `maxEdge` overrides the pantry default: a screenshot in a bug report wants to stay legible. */
export async function prepareImage(file: File, maxEdge = MAX_EDGE): Promise<Blob> {
  if (!file.type.startsWith('image/')) {
    throw new Error('That file is not an image.')
  }

  const bitmap = await createImageBitmap(file)
  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height))
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not process that image.')
    ctx.drawImage(bitmap, 0, 0, width, height)

    const type = pickType()
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, type, QUALITY),
    )
    if (!blob) throw new Error('Could not process that image.')
    return blob
  } finally {
    bitmap.close()
  }
}
